import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { downloadFile, useAnalytics } from '../api/admin'
import { useCompanies, useTemplates } from '../api/hooks'
import { Empty, Stat, gradeColor } from '../components/ui'
import DateInput from '../components/DateInput'

const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#4a3aa7', '#e34948', '#008300', '#9aa3ad']

export default function AnalyticsPage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const set = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); setParams(n) }
  const filters = { from: params.get('from') ?? '', to: params.get('to') ?? '', companyId: params.get('companyId') ?? '', templateId: params.get('templateId') ?? '' }
  const a = useAnalytics(filters)
  const companies = useCompanies()
  const templates = useTemplates()
  const [downloading, setDownloading] = useState(false)
  const d = a.data
  const qs = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')

  const trendData = d ? mergeTrends(d.companyTrends) : []

  return (
    <div className="stack">
      <div className="row between wrap">
        <h2>{t('analytics.title')}</h2>
        <button className="secondary" disabled={downloading} onClick={() => { setDownloading(true); downloadFile(`/v1/analytics/export?${qs}`, 'analitik.xlsx').finally(() => setDownloading(false)) }}>{t('analytics.downloadExcel')}</button>
      </div>
      <div className="card filters">
        <label>{t('reports.from')}<DateInput value={filters.from} onChange={(v) => set('from', v)} /></label>
        <label>{t('reports.to')}<DateInput value={filters.to} min={filters.from || undefined} onChange={(v) => set('to', v)} /></label>
        <label>{t('companies.name')}
          <select value={filters.companyId} onChange={(e) => set('companyId', e.target.value)}>
            <option value="">{t('common.allCompanies')}</option>
            {companies.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label>{t('reports.template')}
          <select value={filters.templateId} onChange={(e) => set('templateId', e.target.value)}>
            <option value="">{t('reports.allTemplates')}</option>
            {templates.data?.map((x) => <option key={x.id} value={x.id}>{x.code}</option>)}
          </select>
        </label>
      </div>
      {!d ? <p>{t('common.loading')}</p> : (
        <>
          <p className="muted small">{t('analytics.period', { from: d.from, to: d.to })}</p>
          <div className="grid stats">
            <Stat label={t('analytics.reports')} value={d.totals.reportCount} hint={t('analytics.scoredCount', { count: d.totals.scoredReportCount })} />
            <Stat label={t('analytics.avgScore')} value={d.totals.averagePercent != null ? `%${d.totals.averagePercent}` : '–'} tone={d.totals.averagePercent == null ? undefined : d.totals.averagePercent >= 85 ? 'good' : d.totals.averagePercent >= 70 ? 'warn' : 'bad'} />
            <Stat label={t('analytics.findings')} value={d.totals.findingCount} hint={`${d.totals.majorCount} ${t('severity.Major')} · ${d.totals.minorCount} ${t('severity.Minor')} · ${d.totals.zeroToleranceCount} ${t('severity.ZeroTolerance')}`} />
            <Stat label={t('analytics.openActions')} value={d.totals.openActions} tone={d.totals.overdueActions > 0 ? 'bad' : undefined} hint={t('analytics.overdueCount', { count: d.totals.overdueActions })} />
            <Stat label={t('analytics.closureDays')} value={d.totals.averageClosureDays ?? '–'} hint={d.totals.onTimeClosureRate != null ? t('analytics.onTime', { rate: d.totals.onTimeClosureRate }) : undefined} />
          </div>

          <div className="grid two">
            <div className="card">
              <h3>{t('analytics.monthly')}</h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={d.scoreByMonth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="averagePercent" name={t('analytics.avgScore')} stroke={SERIES[0]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={d.scoreByMonth}>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="reportCount" name={t('analytics.reports')} fill={SERIES[2]} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="findingCount" name={t('analytics.findings')} fill={SERIES[1]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <h3>{t('analytics.companyTrend')}</h3>
              {d.companyTrends.length === 0 ? <Empty text={t('dashboard.noScoredReports')} /> : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    {d.companyTrends.map((c, i) => <Line key={c.companyId} type="monotone" dataKey={c.companyName} stroke={SERIES[i % SERIES.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />)}
                  </LineChart>
                </ResponsiveContainer>
              )}
              <h3 style={{ marginTop: 12 }}>{t('dashboard.gradeDistribution')}</h3>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={d.gradeDistribution}>
                  <XAxis dataKey="grade" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name={t('dashboard.reports')} radius={[4, 4, 0, 0]} fill={SERIES[0]} label={{ position: 'top', fontSize: 11 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card">
            <h3>{t('analytics.heatmap')}</h3>
            {d.groupHeatmap.rows.length === 0 ? <Empty text={t('dashboard.noScoredReports')} /> : (
              <div className="table-wrap">
                <table className="table compact heatmap">
                  <thead><tr><th>{t('companies.name')}</th>{d.groupHeatmap.groups.map((g) => <th key={g.number} title={g.name} className="num">{g.number}</th>)}</tr></thead>
                  <tbody>
                    {d.groupHeatmap.rows.map((r) => (
                      <tr key={r.companyId}>
                        <td><b>{r.companyName}</b></td>
                        {r.cells.map((c) => <td key={c.number} className="num" style={{ background: heat(c.percent), color: c.percent != null && c.percent < 60 ? '#fff' : undefined }} title={`${d.groupHeatmap.groups.find((g) => g.number === c.number)?.name} · n=${c.sampleCount}`}>{c.percent ?? '–'}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="muted small" style={{ marginTop: 6 }}>{d.groupHeatmap.groups.map((g) => `${g.number}: ${g.name}`).join(' · ')}</div>
              </div>
            )}
          </div>

          <div className="grid two">
            <div className="card">
              <h3>{t('analytics.findingsByGroup')}</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={d.findingsByGroup} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="number" tick={{ fontSize: 11 }} width={30} />
                  <Tooltip formatter={(v, n, p) => [v, `${n} — ${(p.payload as { name: string }).name}`]} />
                  <Legend />
                  <Bar dataKey="major" stackId="a" name={t('severity.Major')} fill="#e34948" />
                  <Bar dataKey="minor" stackId="a" name={t('severity.Minor')} fill="#eda100" />
                  <Bar dataKey="zeroTolerance" stackId="a" name={t('severity.ZeroTolerance')} fill="#4a3aa7" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <h3>{t('analytics.topQuestions')}</h3>
              {d.topQuestions.length === 0 ? <Empty text={t('analytics.noFindings')} /> : (
                <table className="table compact">
                  <thead><tr><th>{t('reports.template')}</th><th>{t('builder.no')}</th><th>{t('builder.text')}</th><th className="num">{t('analytics.findings')}</th></tr></thead>
                  <tbody>{d.topQuestions.map((q, i) => <tr key={i}><td className="small">{q.templateCode}</td><td><b>{q.questionNo}</b></td><td className="small">{q.text.length > 120 ? q.text.slice(0, 120) + '…' : q.text}</td><td className="num">{q.findingCount}</td></tr>)}</tbody>
                </table>
              )}
            </div>
          </div>

          <div className="grid two">
            <div className="card">
              <h3>{t('analytics.auditors')}</h3>
              <table className="table compact">
                <thead><tr><th>{t('reports.auditors')}</th><th className="num">{t('analytics.reports')}</th><th className="num">{t('analytics.avgScore')}</th><th className="num">{t('analytics.findings')}</th></tr></thead>
                <tbody>{d.auditors.map((x, i) => <tr key={i}><td>{x.name}</td><td className="num">{x.auditCount}</td><td className="num">{x.averagePercent != null ? `%${x.averagePercent}` : '–'}</td><td className="num">{x.findingCount}</td></tr>)}</tbody>
              </table>
            </div>
            <div className="card">
              <h3>{t('analytics.actionsByCompany')}</h3>
              <table className="table compact">
                <thead><tr><th>{t('companies.name')}</th><th className="num">{t('actionStatus.Open')}</th><th className="num">{t('actionStatus.Closed')}</th><th className="num">{t('actionStatus.Overdue')}</th></tr></thead>
                <tbody>{d.actionsByCompany.map((c) => <tr key={c.companyId}><td><Link to={`/actions?companyId=${c.companyId}`}>{c.companyName}</Link></td><td className="num">{c.open}</td><td className="num">{c.closed}</td><td className="num" style={{ color: c.overdue ? '#e34948' : undefined }}>{c.overdue}</td></tr>)}</tbody>
              </table>
              <div className="row wrap" style={{ marginTop: 8 }}>{d.actionsByStatus.filter((s) => s.count > 0).map((s) => <span key={s.status} className="badge">{t(`actionStatus.${s.status}`)}: {s.count}</span>)}</div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function mergeTrends(trends: { companyName: string; points: { date: string; percent: number }[] }[]) {
  const map = new Map<string, Record<string, string | number>>()
  for (const c of trends) for (const p of c.points) map.set(p.date, { ...(map.get(p.date) ?? { date: p.date }), [c.companyName]: p.percent })
  return [...map.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)))
}

function heat(p?: number): string | undefined {
  if (p == null) return undefined
  if (p >= 90) return gradeColor.A + '33'
  if (p >= 75) return gradeColor.B + '33'
  if (p >= 60) return gradeColor.C + '55'
  return gradeColor.E
}
